import * as Yup from 'yup'
import {BaseEntity} from '../../../../shared/models/ApiModels'

/** Menü ağacı: farklı yazılımların menülerini ayırır */
export interface IMenuTreeModel {
  id: string
  name: string
  description?: string | null
  /** Ağacın altındaki menü sayısı (alt menüler dahil) */
  menuCount: number
}

/** Henüz bir ağaca bağlanmamış (ağaç özelliğinden önce oluşturulmuş) menüler için seçim değeri */
export const UNASSIGNED_TREE = '__unassigned__'

export interface IMenuModel extends BaseEntity {
  title: string
  icon: string
  path: string
  parentId: string | null
  orderIndex: number
  isActive: boolean
  componentName?: string | null
  menuTreeId?: string | null
  children?: IMenuModel[]
}

export interface IMenuFormValues {
  id?: string
  title: string
  icon: string
  path: string
  parentId: string | null
  isActive: boolean
  componentName: string
  /** Yalnızca kök menüde seçilir; alt menüler üst menünün ağacında olur */
  menuTreeId: string | null
}

export interface IMenuTreeItem extends IMenuModel {
  children: IMenuTreeItem[]
  depth: number
}

export interface IMenuReorderRequest {
  id: string
  parentId: string | null
  orderIndex: number
}

export const validationMenu = Yup.object().shape({
  // Kök menü bir ağaca bağlanmalı; alt menü üst menünün ağacını alır
  menuTreeId: Yup.string()
    .nullable()
    .test('tree-required', 'Menü ağacı seçiniz', function (value) {
      return !!this.parent?.parentId || !!value
    }),
  title: Yup.string().required('Menü adı zorunludur'),
  path: Yup.string().required('Yol (path) zorunludur'),
})

export const initialMenuFormValues: IMenuFormValues = {
  title: '',
  icon: '',
  path: '',
  parentId: null,
  isActive: true,
  componentName: '',
  menuTreeId: null,
}

export const ICON_OPTIONS = [
  {value: '', label: 'İkon Seçiniz'},
  {value: 'bi-question-circle', label: 'Soru İşareti'},
  {value: 'bi-patch-question', label: 'Soru İşareti (Dolu)'},
  {value: 'bi-briefcase', label: 'Çanta'},
  {value: 'bi-key-fill', label: 'Anahtar'},
  {value: 'bi-car-front', label: 'Araç'},
  {value: 'bi-tools', label: 'Araçlar'},
  {value: 'bi-plus-circle', label: 'Artı'},
  {value: 'bi-plus-circle-fill', label: 'Artı (Dolu)'},
  {value: 'bi-gear-fill', label: 'Ayarlar'},
  {value: 'bi-bank', label: 'Banka'},
  {value: 'bi-bank2', label: 'Banka 2'},
  {value: 'bi-bell-fill', label: 'Bildirim'},
  {value: 'bi-ticket-perforated', label: 'Bilet'},
  {value: 'bi-building', label: 'Bina'},
  {value: 'bi-wallet2', label: 'Cüzdan'},
  {value: 'bi-box-arrow-right', label: 'Çıkış'},
  {value: 'bi-ticket-detailed', label: 'Detaylı Bilet'},
  {value: 'bi-diagram-3', label: 'Diyagram'},
  {value: 'bi-currency-dollar', label: 'Dolar'},
  {value: 'bi-file-earmark-text', label: 'Dosya'},
  {value: 'bi-file-earmark-ruled', label: 'Dosya Çizgili'},
  {value: 'bi-file-earmark-bar-graph', label: 'Dosya Grafik'},
  {value: 'bi-file-earmark-check', label: 'Dosya Onay'},
  {value: 'bi-tag-fill', label: 'Etiket'},
  {value: 'bi-house-fill', label: 'Ev'},
  {value: 'bi-receipt-cutoff', label: 'Fatura'},
  {value: 'bi-arrows-expand', label: 'Genişlet'},
  {value: 'bi-arrow-return-left', label: 'Geri Dönüş'},
  {value: 'bi-list-task', label: 'Görev Listesi'},
  {value: 'bi-speedometer2', label: 'Gösterge Paneli'},
  {value: 'bi-graph-up', label: 'Grafik'},
  {value: 'bi-grid-3x3-gap', label: 'Grid'},
  {value: 'bi-grid-3x3-gap-fill', label: 'Grid (Dolu)'},
  {value: 'bi-shield-check', label: 'Güvenlik'},
  {value: 'bi-shield-lock', label: 'Güvenlik Kilit'},
  {value: 'bi-map', label: 'Harita'},
  {value: 'bi-pin-map', label: 'Harita Pini'},
  {value: 'bi-arrow-left-right', label: 'Hareket'},
  {value: 'bi-calculator', label: 'Hesap Makinesi'},
  {value: 'bi-arrow-down-circle', label: 'İndirme'},
  {value: 'bi-truck', label: 'Kamyon'},
  {value: 'bi-card-list', label: 'Kart Listesi'},
  {value: 'bi-person-vcard', label: 'Kartvizit'},
  {value: 'bi-person-badge', label: 'Kimlik'},
  {value: 'bi-person-badge-fill', label: 'Kimlik (Dolu)'},
  {value: 'bi-people-fill', label: 'Kişiler'},
  {value: 'bi-person-plus', label: 'Kişi Ekle'},
  {value: 'bi-folder2-open', label: 'Klasör'},
  {value: 'bi-code-square', label: 'Kod'},
  {value: 'bi-collection', label: 'Koleksiyon'},
  {value: 'bi-geo-alt', label: 'Konum'},
  {value: 'bi-clipboard-check', label: 'Kontrol Listesi'},
  {value: 'bi-credit-card', label: 'Kredi Kartı'},
  {value: 'bi-credit-card-2-front', label: 'Kredi Kartı (Ön)'},
  {value: 'bi-person-gear', label: 'Kullanıcı Ayar'},
  {value: 'bi-box-seam', label: 'Kutu'},
  {value: 'bi-receipt', label: 'Makbuz'},
  {value: 'bi-shop', label: 'Mağaza'},
  {value: 'bi-award', label: 'Marka/Ödül'},
  {value: 'bi-file-text', label: 'Metin Dosyası'},
  {value: 'bi-cash-coin', label: 'Nakit'},
  {value: 'bi-check-circle', label: 'Onay'},
  {value: 'bi-cash-stack', label: 'Para'},
  {value: 'bi-p-circle', label: 'Park'},
  {value: 'bi-clipboard-data', label: 'Rapor'},
  {value: 'bi-cart-check-fill', label: 'Sepet'},
  {value: 'bi-wrench-adjustable-circle-fill', label: 'Servis'},
  {value: 'bi-layout-text-sidebar', label: 'Sidebar'},
  {value: 'bi-list-ul', label: 'Sıralı Liste'},
  {value: 'bi-table', label: 'Tablo'},
  {value: 'bi-calendar-event', label: 'Takvim Etkinlik'},
  {value: 'bi-calendar-plus', label: 'Takvim Ekle'},
  {value: 'bi-calendar-check', label: 'Takvim Onay'},
  {value: 'bi-wrench', label: 'Tamir'},
  {value: 'bi-exclamation-triangle', label: 'Uyarı'},
  {value: 'bi-shop-window', label: 'Vitrin'},
  {value: 'bi-fuel-pump', label: 'Yakıt'},
  {value: 'bi-ban', label: 'Yasak'},
  {value: 'bi-file-earmark-plus', label: 'Yeni Dosya'},
  {value: 'bi-percent', label: 'Yüzde'},
]

export const COMPONENT_OPTIONS = [
  {value: '', label: 'Component Seçiniz'},
  {value: 'CarPage', label: 'Araç Sayfası'},
  {value: 'CarListPage', label: 'Araç Listesi'},
  {value: 'CarStatusListPage', label: 'Araç Durum Listesi'},
  {value: 'BafPage', label: 'Baf İşlemi'},
  {value: 'VehicleSalePage', label: 'Araç Satış'},
  {value: 'MaintenancePage', label: 'Servis Bakım'},
  {value: 'RentPage', label: 'Kira Sayfası'},
  {value: 'RentalListPage', label: 'Kira Listesi'},
  {value: 'ReservationPage', label: 'Rezervasyon'},
  {value: 'ReservationListPage', label: 'Rezervasyon Listesi'},
  {value: 'CustomerPage', label: 'Cari Sayfası'},
  {value: 'CustomerListPage', label: 'Müşteri Listeleme'},
  {value: 'GeneralExpensePage', label: 'Genel Gider'},
  {value: 'InsuranceExpensePage', label: 'Sigorta Gideri'},
  {value: 'CarMTVPage', label: 'MTV Gideri'},
  {value: 'InspectionExpensePage', label: 'Muayene Gideri'},
  {value: 'InvoicePage', label: 'Fatura İşlemleri'},
  {value: 'CreditCardCollectionPage', label: 'Kredi Kartı / Havale'},
  {value: 'CashRegisterPage', label: 'Nakit İşlemler'},
  {value: 'BankTransactionPage', label: 'Banka İşlemleri'},
  {value: 'VehicleGroupPage', label: 'Araç Grubu'},
  {value: 'BrandPage', label: 'Marka'},
  {value: 'VehicleTypePage', label: 'Araç Tipi'},
  {value: 'BranchPage', label: 'Şube'},
  {value: 'LocationPage', label: 'Lokasyon'},
  {value: 'ReservationSourcePage', label: 'Rezervasyon Kaynağı'},
  {value: 'XmlTariffPage', label: 'Xml Tarife'},
  {value: 'XmlTariffDetailPage', label: 'Xml Tarife Detay'},
  {value: 'GeneralTariffPage', label: 'Genel Tarife'},
  {value: 'ServiceTariffPage', label: 'Sigorta Tarifeleri'},
  {value: 'RentalTermPage', label: 'Kiralama Şartları'},
  {value: 'StopSellPage', label: 'Stop Sell'},
  {value: 'OfferPage', label: 'Fiyat Kampanya'},
  {value: 'CouponPage', label: 'Kupon'},
  {value: 'CouponDetailsPage', label: 'Detaylı Kupon'},
  {value: 'VehicleOwnerPage', label: 'Araç Sahibi'},
  {value: 'ExpensePage', label: 'Gider Tanımlama'},
  {value: 'CurrencyPage', label: 'Döviz'},
  {value: 'BankAccountPage', label: 'Banka Hesabı'},
  {value: 'PersonnelPage', label: 'Personel'},
  {value: 'MenuPage', label: 'Menü Yönetimi'},
  {value: 'RolePage', label: 'Rol ve Yetki'},
  {value: 'UserPage', label: 'Kullanıcı Yönetimi'},
]

// Flat listeyi ağaç yapısına dönüştür
export function buildMenuTree(items: IMenuModel[] | undefined | null): IMenuTreeItem[] {
  if (!Array.isArray(items)) return []
  const map = new Map<string, IMenuTreeItem>()
  const roots: IMenuTreeItem[] = []

  // Tüm elemanları map'e ekle
  items.forEach((item) => {
    map.set(item.id!, {...item, children: [], depth: 0})
  })

  // Parent-child ilişkilerini kur
  items.forEach((item) => {
    const node = map.get(item.id!)!
    if (item.parentId && map.has(item.parentId)) {
      const parent = map.get(item.parentId)!
      node.depth = parent.depth + 1
      parent.children.push(node)
    } else {
      roots.push(node)
    }
  })

  // Her seviyede orderIndex'e göre sırala
  const sortChildren = (nodes: IMenuTreeItem[]) => {
    nodes.sort((a, b) => a.orderIndex - b.orderIndex)
    nodes.forEach((node) => sortChildren(node.children))
  }
  sortChildren(roots)

  return roots
}

// Ağaç yapısını flat listeye dönüştür (depth bilgisiyle)
export function flattenMenuTree(tree: IMenuTreeItem[], depth = 0): IMenuTreeItem[] {
  const result: IMenuTreeItem[] = []
  tree.forEach((node) => {
    result.push({...node, depth})
    if (node.children.length > 0) {
      result.push(...flattenMenuTree(node.children, depth + 1))
    }
  })
  return result
}
